/*
 * Utilidades de eventos del calendario (fechas iCal, categorías, formato).
 * Las usan la vista del calendario y el widget de próximos eventos de la portada.
 */

export const CATEGORIES = [
  { key: "misas", label: "Misas", color: "#3b82f6" },
  { key: "sacramentos", label: "Sacramentos", color: "#f59e0b" },
  { key: "comunidad", label: "Comunidad", color: "#22c55e" },
  { key: "juventud", label: "Juventud", color: "#a855f7" },
];

// Palabras clave para clasificar cada evento (sin tildes ni mayúsculas).
// El orden importa: "Misa de jóvenes" cae en juventud antes que en misas.
const CATEGORY_KEYWORDS = [
  ["juventud", ["joven", "juvenil", "juventud", "catequesis", "confirmando", "scout", "ninos"]],
  ["sacramentos", ["bautiz", "boda", "matrimonio", "comunion", "confirmacion", "confesion", "penitencia", "uncion"]],
  ["misas", ["misa", "eucarist", "adoracion", "rosario", "visperas", "novena", "triduo", "vigilia", "procesion"]],
];

export const MONTH_NAMES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
export const MONTH_SHORT = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];
export const WEEKDAYS_LONG = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

export const DAY_MS = 86400000;

export function normalizeText(str) {
  return (str || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

export function unescapeICS(str) {
  return (str || "")
    .toString()
    .replace(/\\n/gi, "\n")
    .replace(/\\([,;\\])/g, "$1")
    .trim();
}

export function sameDay(a, b) {
  return (
    !!a && !!b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Fechas iCalendar:
 *  - 20260315            -> día completo
 *  - 20260315T100000Z    -> UTC
 *  - 20260315T100000     -> hora local
 * y, como alternativa, cualquier fecha ISO.
 */
export function parseICS(str) {
  if (!str) return null;
  const s = String(str).trim();

  let m = s.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (m) return { date: new Date(+m[1], +m[2] - 1, +m[3]), allDay: true };

  m = s.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})?(Z)?$/i);
  if (m) {
    const parts = [+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0)];
    const date = m[7] ? new Date(Date.UTC(...parts)) : new Date(...parts);
    return { date, allDay: false };
  }

  const date = new Date(s);
  return isNaN(date) ? null : { date, allDay: false };
}

export function detectCategory(title, description) {
  const text = normalizeText(`${title} ${description}`);
  const found = CATEGORY_KEYWORDS.find(([, words]) => words.some((w) => text.includes(w)));
  return found ? found[0] : "comunidad";
}

export function getCategory(key) {
  return CATEGORIES.find((c) => c.key === key) || { key, label: "", color: "#64748b" };
}

// Evento de la API -> objeto normalizado (null si no tiene fecha válida)
export function normalizeEvent(e, idx = 0) {
  const start = parseICS(e.inicio);
  if (!start) return null;
  const end = parseICS(e.fin);
  const title = unescapeICS(e.titulo) || "Evento";
  const description = unescapeICS(e.descripcion);
  const location = unescapeICS(e.ubicacion || e.lugar || e.location);
  return {
    id: `${idx}-${e.inicio}`,
    title,
    description,
    location,
    start: start.date,
    end: end ? end.date : null,
    allDay: start.allDay,
    category: detectCategory(title, description),
    searchText: normalizeText(`${title} ${description} ${location}`),
  };
}

export function formatTime(d) {
  return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
}

export function longDate(d) {
  return `${WEEKDAYS_LONG[d.getDay()].toLowerCase()} ${d.getDate()} de ${MONTH_NAMES[d.getMonth()].toLowerCase()} de ${d.getFullYear()}`;
}

export function timeRange(e) {
  if (e.allDay) {
    if (e.end && e.end - e.start > DAY_MS) {
      const last = new Date(e.end.getTime() - 1);
      return `Todo el día · hasta el ${last.getDate()} de ${MONTH_NAMES[last.getMonth()].toLowerCase()}`;
    }
    return "Todo el día";
  }
  if (!e.end) return formatTime(e.start);
  if (sameDay(e.start, e.end)) return `${formatTime(e.start)} – ${formatTime(e.end)}`;
  return `${formatTime(e.start)} – ${e.end.getDate()}/${e.end.getMonth() + 1} ${formatTime(e.end)}`;
}

export function googleCalendarLink(e) {
  const pad = (n) => String(n).padStart(2, "0");
  const ymd = (d) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  const utc = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  let dates;
  if (e.allDay) {
    const end = e.end || new Date(e.start.getTime() + DAY_MS);
    dates = `${ymd(e.start)}/${ymd(end)}`;
  } else {
    const end = e.end || new Date(e.start.getTime() + 3600000);
    dates = `${utc(e.start)}/${utc(end)}`;
  }
  const params = new URLSearchParams({ action: "TEMPLATE", text: e.title, dates });
  if (e.description) params.set("details", e.description);
  if (e.location) params.set("location", e.location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

// Date -> "YYYY-MM-DD" (para enlazar un día concreto del calendario)
export function toISODate(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Enlace para compartir un evento: abre ese día en el calendario y da título a
// la vista previa de WhatsApp/Facebook (public/compartir.php)
export function rutaEvento(e) {
  return `/Eventos?fecha=${toISODate(e.start)}&evento=${encodeURIComponent(e.title || "")}`;
}

// "Sábado 24 de diciembre · 00:00 – 01:00 · Iglesia de Santo Domingo"
export function textoEvento(e) {
  const fecha = longDate(e.start).replace(/ de \d{4}$/, "");
  return [fecha.charAt(0).toUpperCase() + fecha.slice(1), timeRange(e), e.location].filter(Boolean).join(" · ");
}
